import { Box } from "@mui/system"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import { TableHeaderItem } from "src/components/mypage/TableHeaderItem"
import { useLoginContext } from "src/hooks/useLoginContext"
import { ADVISE_TYPE, CONSULT_STATUS } from "src/modules/const"
import { getLabel } from "src/modules/helper"
import { Consult } from "src/openapi"
import { Button, Grid, Typography } from "src/UILibrary"
import { ConsultAcceptModal } from "./ConsultAcceptModal"
import { ConsultCancelAdviserModal } from "./ConsultCancelAdviserModal"
import { ConsultResignModal } from "./ConsultResignModal"
import { ConsultStatusLabel } from "./ConsultStatusLabel"
import { ConsultUserTableItem } from "./ConsultUserTableItem"
import "./consult.scss"

type Props = {
  item: Consult
  handleItem: any
  closeDetail: any
}

export const ConsultAdviserDetailPage = ({ item, handleItem, closeDetail }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.inbox" })

  const isPC = useMediaQuery({ query: "(min-width: 1400px)" })
  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })
  const isSmall = useMediaQuery({ query: "(min-width: 700px)" })
  const isExtraSmall = useMediaQuery({ query: "(min-width: 450px)" })

  const { startupStages, startupPlans, getAreaDetail } = useLoginContext()

  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false)
  const [openResignModal, setOpenResignModal] = useState<boolean>(false)
  const [openAccetModal, setOpenAcceptModal] = useState<boolean>(false)

  let headers: any[] = [
    {
      size: isPC ? 2 : isTablet ? 2 : 2,
      name: t("date"),
      sortby: "",
    },
    {
      size: isPC ? 2 : isTablet ? 2 : 2,
      name: t("consult_status"),
      sortby: "",
    },
    {
      size: isPC ? 2 : isTablet ? 2 : 2,
      name: t("member_name"),
      sortby: "",
    },
    {
      size: isPC ? 2 : isTablet ? 2 : 2,
      name: t("reserved_date"),
      sortby: "",
    },
    {
      size: isPC ? 4 : isTablet ? 4 : 2,
      name: t("content"),
      sortby: "",
    },
  ]

  const getMethodString = () => {
    switch (item.method) {
      case ADVISE_TYPE.MEETING:
        return t("consult_method_meeting")
      case ADVISE_TYPE.CALL:
        return t("consult_method_call")
      case ADVISE_TYPE.ONLINE:
        return t("consult_method_online")
    }
    return ""
  }

  return (
    <Box
      sx={{
        ...(isTablet
          ? { height: "auto", pt: "0px", pl: "20px" }
          : { width: "100%", position: "absolute", top: 60, bottom: 120 }),
        overflowY: "scroll",
      }}
    >
      <Typography.FileFieldDetail
        sx={{
          fontFamily: "Noto Sans JP !important",
          my: "12px",
          ml: "20px",
          ":hover": { opacity: 0.8, cursor: "pointer" },
        }}
        onClick={() => closeDetail()}
      >
        {"＜　"}
        {t("page_back")}
      </Typography.FileFieldDetail>
      {isTablet ? (
        <Grid container sx={{ mt: "0px", height: "32px", borderBottom: "1px dotted #D4D3D7" }}>
          {headers.map((header, index) => (
            <TableHeaderItem
              key={index}
              name={header.name}
              size={header.size}
              sortby={header.sortby}
            />
          ))}
        </Grid>
      ) : (
        <></>
      )}
      <ConsultUserTableItem item={item} handleSelected={() => {}} />
      <Box sx={{ px: "20px", pt: "20px", display: "flex", flexWrap: "wrap" }}>
        {isSmall && (
          <Typography.DetailText sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989" }}>
            {t("status")}
          </Typography.DetailText>
        )}
        <Typography.DetailText
          className="status-label"
          sx={{
            fontFamily: "Noto Sans JP !important",
            color: "#C69989",
            ml: isSmall ? "30px" : "0px",
            mr: "10px",
          }}
        >
          {item.createdAt?.slice(0, 10)}
        </Typography.DetailText>
        <ConsultStatusLabel status={CONSULT_STATUS.RESERVED} />
        <Typography.DetailText
          className="status-label"
          sx={{ fontFamily: "Noto Sans JP !important", color: "#D4D3D7", mx: "10px" }}
        >
          {">"}
        </Typography.DetailText>
        {item.status === CONSULT_STATUS.RESERVED && (
          <Typography.DetailText
            className="status-label"
            sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989" }}
          >
            {t("confirming")}
          </Typography.DetailText>
        )}
        {item.status === CONSULT_STATUS.ACCEPTED && (
          <>
            <Typography.DetailText
              className="status-label"
              sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "10px" }}
            >
              {item.reservedDate?.slice(0, 10)}
            </Typography.DetailText>
            <ConsultStatusLabel status={item.status as string} />
          </>
        )}
        {item.status === CONSULT_STATUS.CANCELED && (
          <>
            {item.reservedDate?.slice(0, 10) !== "0001-01-01" && (
              <>
                <Typography.DetailText
                  className="status-label"
                  sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "10px" }}
                >
                  {item.reservedDate?.slice(0, 16).replace("T", " ")}
                </Typography.DetailText>
                <ConsultStatusLabel status={CONSULT_STATUS.ACCEPTED} />
                <Typography.DetailText
                  className="status-label"
                  sx={{ fontFamily: "Noto Sans JP !important", color: "#D4D3D7", mx: "10px" }}
                >
                  {">"}
                </Typography.DetailText>
              </>
            )}
            <>
              <Typography.DetailText
                className="status-label"
                sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "10px" }}
              >
                {item.updatedAt?.slice(0, 10)}
              </Typography.DetailText>
              <ConsultStatusLabel status={item.status as string} />
            </>
          </>
        )}
        {item.status === CONSULT_STATUS.FINISHED && (
          <>
            <Typography.DetailText
              className="status-label"
              sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "10px" }}
            >
              {item.reservedDate?.slice(0, 16).replace("T", " ")}
            </Typography.DetailText>
            <ConsultStatusLabel status={CONSULT_STATUS.ACCEPTED} />
            <Typography.DetailText
              className="status-label"
              sx={{ fontFamily: "Noto Sans JP !important", color: "#D4D3D7", mx: "10px" }}
            >
              {">"}
            </Typography.DetailText>
            <Typography.DetailText
              className="status-label"
              sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "10px" }}
            >
              {item.updatedAt?.slice(0, 10)}
            </Typography.DetailText>
            <ConsultStatusLabel status={item.status as string} />
          </>
        )}
        {item.status === CONSULT_STATUS.RESIGNED && (
          <>
            <Typography.DetailText
              className="status-label"
              sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "10px" }}
            >
              {item.updatedAt?.slice(0, 10)}
            </Typography.DetailText>
            <ConsultStatusLabel status={item.status as string} />
          </>
        )}
      </Box>
      {item.status === CONSULT_STATUS.CANCELED && (
        <Box sx={{ p: "20px" }}>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "10px" }}
          >
            {t("consult_is_cancelled")}
          </Typography.InputFieldTitle>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontSize: "400", mt: "20px" }}
          >
            {t("consult_cancel_reason_is_below")}
          </Typography.InputFieldTitle>
          <Typography.InputFieldTitle
            sx={{
              fontFamily: "Noto Sans JP !important",
              fontSize: "400",
              mt: "20px",
            }}
          >
            {item.cancelReason}
          </Typography.InputFieldTitle>
        </Box>
      )}
      {item.status === CONSULT_STATUS.RESERVED && (
        <Box sx={{ p: "20px" }}>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "10px" }}
          >
            {t("has_new_consult")}
          </Typography.InputFieldTitle>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontSize: "400", mt: "20px" }}
          >
            {t("please_reply_to_new_consult")}
          </Typography.InputFieldTitle>
          <Box display={isExtraSmall ? "flex" : "block"} sx={{ mt: "20px" }}>
            <Button
              variant="contained"
              sx={{
                width: isTablet ? "240px" : "150px",
                fontSize: "1rem",
                fontFamily: "Noto Sans JP !important",
                lineHeight: "1rem",
                fontWeight: 700,
                display: "flex",
                justifyContent: "center",
                borderRadius: 0,
                ":hover": {
                  bgcolor: "#2DA389",
                },
                height: "48px",
              }}
              onClick={() => setOpenAcceptModal(true)}
            >
              {t("accept_consult")}
            </Button>
            <OutlinedButton
              caption={t("resign_consult")}
              fontColor="primary.main"
              fontWeight="700"
              fontSize={16}
              sx={{
                borderColor: "primary.main",
                width: isTablet ? "240px" : "150px",
                fontSize: "1rem",
                fontFamily: "Noto Sans JP !important",
                lineHeight: "1rem",
                fontWeight: 700,
                mt: isExtraSmall ? "0px" : "20px",
                ml: isExtraSmall ? "20px" : "0px",
                display: "flex",
                justifyContent: "center",
                borderRadius: 0,
                py: "15px !important",
                height: "48px",
              }}
              onClick={() => setOpenResignModal(true)}
            />
          </Box>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              fontWeight: "700",
              color: "#FC3317",
              mt: "20px",
            }}
          >
            {t("resign_cannot_recover")}
          </Typography.DetailText>
        </Box>
      )}
      {item.status === CONSULT_STATUS.ACCEPTED && (
        <Box sx={{ p: "20px" }}>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "10px" }}
          >
            {t("consult_is_accepted")}
          </Typography.InputFieldTitle>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontSize: "400", mt: "20px" }}
          >
            {t("here_is_consult_detail")}
          </Typography.InputFieldTitle>
          <Typography.InputFieldTitle
            sx={{
              fontFamily: "Noto Sans JP !important",
              fontSize: "400",
              mt: "20px",
              color: "#998E8A",
              textDecoration: "underline",
              ":hover": { opacity: 0.8, cursor: "pointer" },
              width: "fit-content",
            }}
            onClick={() => setOpenCancelModal(true)}
          >
            {t("here_to_cancel_consult")}
          </Typography.InputFieldTitle>
        </Box>
      )}
      {item.status === CONSULT_STATUS.FINISHED && (
        <Box sx={{ p: "20px" }}>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "10px" }}
          >
            {t("consult_is_accepted")}
          </Typography.InputFieldTitle>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontSize: "400", mt: "20px" }}
          >
            {t("here_is_consult_detail")}
          </Typography.InputFieldTitle>
        </Box>
      )}
      {item.status === CONSULT_STATUS.RESIGNED && (
        <Box sx={{ p: "20px" }}>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "10px" }}
          >
            {t("consult_is_cancelled")}
          </Typography.InputFieldTitle>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontSize: "400", mt: "20px" }}
          >
            {t("consult_resign_reason_is_below")}
          </Typography.InputFieldTitle>
          <Typography.InputFieldTitle
            sx={{
              fontFamily: "Noto Sans JP !important",
              fontSize: "400",
              mt: "20px",
            }}
          >
            {item.rejectReason}
          </Typography.InputFieldTitle>
        </Box>
      )}
      {(item.status === CONSULT_STATUS.RESERVED ||
        item.status === CONSULT_STATUS.CANCELED ||
        item.status === CONSULT_STATUS.RESIGNED) && (
        <Box sx={{ mt: "20px", p: "20px" }}>
          <Typography.InputFieldTitle
            sx={{
              fontFamily: "Noto Sans JP !important",
              fontWeight: "700",
              pb: "10px",
              borderBottom: "1px dashed #D4D3D7",
              width: "100%",
            }}
          >
            {t("consult_content")}
          </Typography.InputFieldTitle>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("member_name_to_consult")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
              fontWeight: "700",
            }}
          >
            {`${item.member?.lastName} ${item.member?.firstName}`}
          </Typography.DetailText>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("registered_email")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
              fontWeight: "700",
            }}
          >
            {item.member?.email}
          </Typography.DetailText>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("consult_hope")}
          </Typography.InputFieldTitle>
          <Box sx={{ display: "flex", mt: "12px" }}>
            <Typography.DetailText sx={{ fontFamily: "Noto Sans JP !important", mr: "10px" }}>
              {t("consult_hope_1")}
            </Typography.DetailText>
            <Typography.DetailText
              sx={{
                fontFamily: "Noto Sans JP !important",
                textDecoration: "underline",
              }}
            >
              {item.hopeDate1?.slice(0, 16).replace("T", " ")}
            </Typography.DetailText>
          </Box>
          {item.hopeDate2?.slice(0, 10) !== "0001-01-01" && (
            <Box sx={{ display: "flex", mt: "12px" }}>
              <Typography.DetailText sx={{ fontFamily: "Noto Sans JP !important", mr: "10px" }}>
                {t("consult_hope_2")}
              </Typography.DetailText>
              <Typography.DetailText
                sx={{
                  fontFamily: "Noto Sans JP !important",
                  textDecoration: "underline",
                }}
              >
                {item.hopeDate2?.slice(0, 16).replace("T", " ")}
              </Typography.DetailText>
            </Box>
          )}
          {item.hopeDate3?.slice(0, 10) !== "0001-01-01" && (
            <Box sx={{ display: "flex", mt: "12px" }}>
              <Typography.DetailText sx={{ fontFamily: "Noto Sans JP !important", mr: "10px" }}>
                {t("consult_hope_3")}
              </Typography.DetailText>
              <Typography.DetailText
                sx={{
                  fontFamily: "Noto Sans JP !important",
                  textDecoration: "underline",
                }}
              >
                {item.hopeDate3?.slice(0, 16).replace("T", " ")}
              </Typography.DetailText>
            </Box>
          )}
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("consult_method")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
            }}
          >
            {getMethodString()}
          </Typography.DetailText>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("start_step")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
            }}
          >
            {getLabel(startupStages, item.startupStageId as number)}
          </Typography.DetailText>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("start_plan")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
            }}
          >
            {getLabel(startupPlans, item.startupPlanId as number)}
          </Typography.DetailText>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("business_overview")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
            }}
          >
            {item.startupDescription ? item.startupDescription : t("no_data_word")}
          </Typography.DetailText>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("consultant_summary")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
            }}
          >
            {item.consultDescription ? item.consultDescription : t("no_data_word")}
          </Typography.DetailText>
        </Box>
      )}
      {(item.status === CONSULT_STATUS.ACCEPTED || item.status === CONSULT_STATUS.FINISHED) && (
        <Box sx={{ mt: "20px", p: "20px" }}>
          <Typography.InputFieldTitle
            sx={{
              fontFamily: "Noto Sans JP !important",
              fontWeight: "700",
              pb: "10px",
              borderBottom: "1px dashed #D4D3D7",
              width: "100%",
            }}
          >
            {t("consult_confirm_content")}
          </Typography.InputFieldTitle>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("member_name_to_consult")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
              fontWeight: "700",
            }}
          >
            {`${item.member?.lastName} ${item.member?.firstName}`}
          </Typography.DetailText>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("registered_email")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
              fontWeight: "700",
            }}
          >
            {item.member?.email}
          </Typography.DetailText>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("reserved_date")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
            }}
          >
            {item.reservedDate?.slice(0, 16).replace("T", " ")}
          </Typography.DetailText>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("consult_method")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
            }}
          >
            {getMethodString()}
          </Typography.DetailText>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("start_step")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
            }}
          >
            {getLabel(startupStages, item.startupStageId as number)}
          </Typography.DetailText>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("start_plan")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
            }}
          >
            {getLabel(startupPlans, item.startupPlanId as number)}
          </Typography.DetailText>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("business_overview")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
            }}
          >
            {item.startupDescription ? item.startupDescription : t("no_data_word")}
          </Typography.DetailText>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
          >
            {t("consultant_summary")}
          </Typography.InputFieldTitle>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "12px",
            }}
          >
            {item.consultDescription ? item.consultDescription : t("no_data_word")}
          </Typography.DetailText>
          {item.method === ADVISE_TYPE.ONLINE ? (
            <>
              <Typography.InputFieldTitle
                sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
              >
                {t("zoom_link")}
              </Typography.InputFieldTitle>
              <Typography.DetailText
                sx={{
                  fontFamily: "Noto Sans JP !important",
                  mt: "12px",
                }}
              >
                {t("zoom_link_description")}
              </Typography.DetailText>
              <Typography.DetailText
                sx={{
                  fontFamily: "Noto Sans JP !important",
                  mt: "12px",
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#235496",
                  ":hover": { opacity: 0.8, cursor: "pointer" },
                }}
                onClick={() => window.open(item.meetingUrl, "_blank")}
              >
                {item.meetingUrl}
              </Typography.DetailText>
            </>
          ) : (
            <>
              <Typography.InputFieldTitle
                sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
              >
                {t("consult_area")}
              </Typography.InputFieldTitle>
              <Box display="flex" sx={{ mt: "12px", flexWrap: "wrap" }}>
                <Box sx={{ width: "320px", height: "160px", overflow: "hidden" }}>
                  <iframe
                    src={`https://maps.google.com/maps?q=${
                      getAreaDetail(item.areaId as number).facilityLatitude
                    },${
                      getAreaDetail(item.areaId as number).facilityLongitude
                    }&hl=ja&z=14&output=embed`}
                    style={{ border: 0, width: "100%", height: "320px", marginTop: "-80px" }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", mx: "10px", my: "auto" }}>
                  <Typography.DetailText
                    sx={{
                      fontFamily: "Noto Sans JP !important",
                      mt: "12px",
                    }}
                  >
                    {getAreaDetail(item.areaId as number).facilityAddress}
                  </Typography.DetailText>
                  <Typography.DetailText
                    sx={{
                      fontFamily: "Noto Sans JP !important",
                      mt: "12px",
                      fontSize: "12px",
                      fontWeight: "700",
                      color: "#235496",
                      ":hover": { opacity: 0.8, cursor: "pointer" },
                    }}
                    onClick={() =>
                      window.open(
                        `https://www.google.com/maps/search/?api=1&query=${
                          getAreaDetail(item.areaId as number).facilityLatitude
                        },${getAreaDetail(item.areaId as number).facilityLongitude}`,
                        "_blank"
                      )
                    }
                  >
                    {t("to_google_map")}
                  </Typography.DetailText>
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}
      <ConsultCancelAdviserModal
        open={openCancelModal}
        handleOpen={setOpenCancelModal}
        item={item}
      />
      <ConsultResignModal
        open={openResignModal}
        handleOpen={setOpenResignModal}
        item={item}
        handleItem={handleItem}
      />
      <ConsultAcceptModal
        open={openAccetModal}
        handleOpen={setOpenAcceptModal}
        item={item}
        handleItem={handleItem}
      />
    </Box>
  )
}
